import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal } from "ui-kit/Modal";
import { Button } from "ui-kit/Button";
import { Checkbox } from "ui-kit/PrimitiveControls/Checkbox";
import { Input } from "ui-kit/PrimitiveControls/Input";
import { Artist } from "types/artist";
import { PlatformLinks } from "../NewRelease/hooks/useArtists";
import axios from 'axios';
import {FormattedMessage} from "react-intl";
import IntlStore from "stories/IntlStore";
import {Filter} from "bad-words";

const filter = new Filter();

interface AddArtistModalProps {
  isOpen: boolean;
  artistCreating: boolean;
  onHide: () => void;
  artists: Artist[];
  createArtist: (name: string, links: PlatformLinks[], status: boolean) => Promise<Artist>;
  goToFirstPage: () => void
}

const SpotifyId = '1';
const AppleMusicId = '2';

export const AddArtistModal: React.FC<AddArtistModalProps> = props => {
  const { isOpen, onHide, artists, createArtist, artistCreating } = props;
  const [selectedArtist, setSelectedArtist] = useState<string>('');
  const [hasSpotify, setHasSpotify] = useState<boolean | null>(null);
  const [spotifyUrl, setSpotifyUrl] = useState('');
  const [hasAppleMusic, setHasAppleMusic] = useState<boolean | null>(null);
  const [appleUrl, setAppleUrl] = useState('');
  const [errorData, setErrorData] = useState('')

  const isSaveDisabled =
    selectedArtist.length === 0 ||
    hasSpotify === null ||
    hasAppleMusic === null ||
    (hasSpotify && spotifyUrl.length === 0) ||
    (hasAppleMusic && appleUrl.length === 0);

  const onCreateArtist = async () => {
    const platformLinks = [];
    const checked = false

    if (hasSpotify) {
      platformLinks.push({ social_platform_id: SpotifyId, url: spotifyUrl, status:false });
    }

    if (hasAppleMusic) {
      platformLinks.push({ social_platform_id: AppleMusicId, url: appleUrl, status:false });
    }

    let artist = artists.find((artist) => artist.id === selectedArtist);

    try {
      if (!artist) {
        artist = await createArtist(selectedArtist, platformLinks, checked);
        props.goToFirstPage()
        onHide();
        setHasSpotify(null);
        setHasAppleMusic(null);
        setSelectedArtist('');
        setSpotifyUrl('');
        setAppleUrl('');
      }
    } catch (e) {
      if (axios.isAxiosError(e)) {
        const errors = e.response?.data.errors;
        if (errors?.name?.length) {
          setErrorData(` ${errors.name[0].message}`);
        }
      }
    }
  }

  return (
    <Modal
      visible={isOpen}
      onHide={() => {
        setHasAppleMusic(null)
        setHasSpotify(null)
        onHide();
        
      }}
      dismissableMask
      showHeader={false}
      header={null}
      footer={(
        <Footer>
          <Button loading={artistCreating} disabled={isSaveDisabled} onClick={onCreateArtist}>
            <FormattedMessage
              id="Artists.AddArtist"
              defaultMessage="Add artist"
            />
          </Button>
        </Footer>
      )}
    >
      <FormContainer>
        <Section>
          <div>
            <input
              type="text"
              placeholder={IntlStore.intl.formatMessage({
                id: "Artists.CreateArtist",
                defaultMessage: "Create artist"
              })}
              onChange={(e) => {
                const value = e.target.value;

                if (filter.isProfane(value)) {
                  setErrorData("Please avoid using inappropriate words")
                  return;
                }

                setErrorData('')
                if (value.length >= 3) {
                  setSelectedArtist(value);
                } else {
                  setSelectedArtist("");
                }
              }}
            />
            {errorData && <ErrorBox>{errorData}</ErrorBox>}
          </div>
        </Section>
        <Section>
          <Label>
            <FormattedMessage
              id="Artists.AddArtistModal.Question1"
              defaultMessage="Does this artist have music live on Spotify?"
            />
          </Label>
          <CheckboxGroup>
            <div>
              <Checkbox
                name="spotifyYes"
                label={IntlStore.intl.formatMessage({
                  id: "Artists.AddArtistModal.Yes",
                  defaultMessage: "Yes"
                })}
                checked={hasSpotify === true}
                onChange={() => setHasSpotify(true)}
              />
            </div>
            <div>
              <Checkbox
                name="spotifyNo"
                label={IntlStore.intl.formatMessage({
                  id: "Artists.AddArtistModal.No",
                  defaultMessage: "No"
                })}
                checked={hasSpotify === false}
                onChange={() => setHasSpotify(false)}
              />
            </div>
          </CheckboxGroup>
          {hasSpotify ? (
            <Input
              name="spotifyUrl"
              value={spotifyUrl}
              onChange={(e) => setSpotifyUrl(e.target.value)}
              placeholder={IntlStore.intl.formatMessage({
                id: "Artists.AddArtistModal.SpotifyUrlPlaceholder",
                defaultMessage: "Paste the full URL to your Spotify artist profile"
              })}
            />
          ) : hasSpotify === false && (
            <Warning>
              <FormattedMessage
                id="Artists.AddArtistModal.Warning1"
                defaultMessage="We will create a new profile for this artist on Spotify"
              />
            </Warning>
          )}
        </Section>
        <Section>
          <Label>
            <FormattedMessage
              id="Artists.AddArtistModal.Question2"
              defaultMessage="Does this artist have music live on Apple Music?"
            />
          </Label>
          <CheckboxGroup>
            <div>
              <Checkbox
                name="appleYes"
                label={IntlStore.intl.formatMessage({
                  id: "Artists.AddArtistModal.Yes",
                  defaultMessage: "Yes"
                })}
                checked={hasAppleMusic === true}
                onChange={() => setHasAppleMusic(true)}
              />
            </div>
            <div>
              <Checkbox
                name="appleNo"
                label={IntlStore.intl.formatMessage({
                  id: "Artists.AddArtistModal.No",
                  defaultMessage: "No"
                })}
                checked={hasAppleMusic === false}
                onChange={() => setHasAppleMusic(false)}
              />
            </div>
          </CheckboxGroup>
          {hasAppleMusic ? (
            <Input
              name="appleUrl"
              value={appleUrl}
              onChange={(e) => setAppleUrl(e.target.value)}
              placeholder={IntlStore.intl.formatMessage({
                id: "Artists.AddArtistModal.AppleUrlPlaceholder",
                defaultMessage: "Paste the full URL to your Apple Music artist page"
              })}
            />
          ) : hasAppleMusic === false && (
            <Warning>
              <FormattedMessage
                id="Artists.AddArtistModal.Warning2"
                defaultMessage="We will create a new profile for this artist on Apple Music"
              />
            </Warning>
          )}
        </Section>
      </FormContainer>
    </Modal>
  );
};

const FormContainer = styled.div`
  width: 490px;
  padding: 16px 0 0;

  /* Mobile: do not use fixed width */
  @media (max-width: 768px) {
    width: 100%;
    max-width: 100%;
    max-height: calc(100dvh - 180px);
    overflow: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }

  input{
  width: 100%;
  height: 43px;
  border: 1px solid #E0E0E0;
  border-radius: 10px;
  padding:0 10px;
  box-sizing: border-box;
  }
  
`;

const ErrorBox= styled.div`
  color:red;
  padding-top:2px;
  padding-left:5px;
  font-size:14px;
`

const CheckboxGroup = styled.div`
  display: flex;
  gap: 30px;
  align-items: center;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: 12px 20px;
  }
`;

const Label = styled.div`
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #00000033;
  padding: 12px 16px 0 24px;
  margin: 0 -24px;

  /* Mobile: avoid negative margins causing horizontal overflow */
  @media (max-width: 768px) {
    margin: 0;
    padding: 12px 0 0;
  }
`;

const Warning = styled.div`
  border-radius: 15px;
  padding: 18px 16px;
  background-color: #EFFFF1;
  font-weight: 500;
  font-size: 13px;
  line-height: 18px;
`;


